import * as log4javascript from 'log4javascript';
import PerformanceLevel from "/lib/html5-software-library/src/ranj/performance/PerformanceLevel";
import BrowserDetect from "/lib/html5-software-library/src/ranj/util/BrowserDetect";
import StageManager from "/lib/html5-software-library/src/ranj/performance/StageManager";
import AppInstaller from './AppInstaller';
import MediatorMapping from './MediatorMapping';
import ContextEvent from './ContextEvent';
import Stats from 'stats-js/build/stats';
import GameEvent from './GameEvent';
import { getURLParameter } from '../../../../lib/html5-software-library/src/ranj/util/URLUtil';
import AppModel from './AppModel';

const canvasIds = ['mainGameCanvas'];

export default class Application extends soma.Application {
    constructor() {
        super();
    }

    init() {
        this.log = log4javascript.getLogger(this);
        //TODO: dont use global. We keep this for now, because a lot of classes rely on log being in window scope...
        window.log = this.log;

        this.setupCanvasForMobile();

        // Create the stages and context view
        // Note: having several stages is usually related to optimizing the game for best performance -- See StageManager for details on how to do it.
        // Or a second canvas/stage is used for overlaying video controls
        var enableTouch = createjs.Touch.isSupported();
        this.log.info("App.init: Touch enabled: " + enableTouch);
        var stages = this.createStages(canvasIds, enableTouch);

        // Initialize StageManager (to handle stage updates)
        const fpsDbg = parseInt(getURLParameter("fps"));
        if (!isNaN(fpsDbg)) {
            console.warn("Using fps from query: " + fpsDbg);
        }
        const timingModeDbg = getURLParameter("timingmode");
        if (timingModeDbg) {
            console.warn("Using timingmode from query: " + timingModeDbg);
        }
        const fps = isNaN(fpsDbg) ? 60 : fpsDbg;
        const timingMode = timingModeDbg || createjs.Ticker.TIMEOUT;
        var stageManager = new StageManager(stages, fps, PerformanceLevel.MEDIUM, timingMode);
        stageManager.enableAllStagesMouseOver(BrowserDetect.isMobile() ? 0 : 10);

        this.injector.mapValue("stageManager", stageManager);

        // Create the screenNavigator with the contextView and map it
        // The screen navigator requires a contextView, this we can't map
        // against the class, but instead we need to map a particular instance.
        var stage = stages[canvasIds[0]];
        this.injector.mapValue("stage", stage);

        const mediatorMap = new MediatorMapping();
        this.injector.mapValue("mediatorMap", mediatorMap);

        // Mapping the other dependencies.
        this.injector.createInstance(AppInstaller);

        this.initResizing();

        //For now we dont want sound in all minigames:
        const soundManager = this.injector.getValue('soundManager');
        soundManager.setMute(true);

        //Dont use onunload because it is not properly supported on all platforms and comes too late.
        //NB: onbeforeunload not supported on mobile safari
        window.onbeforeunload = this.destroy.bind(this);

        this.handleVisibilityChangeBound = this.handleVisibilityChange.bind(this);
        document.addEventListener("visibilitychange", this.handleVisibilityChangeBound);

        if (ranj.DEBUG) {
            this.initDebug();
            //add Debug stats
            this.addStats();
            //
            window.app = this;
        }
    }

    start() {
        console.log('App.start');

        this.dispatcher.dispatch(ContextEvent.STARTUP, { data: canvasIds });
    }

    destroy() {
        console.log("app destroy");
        this.dispatcher.dispatch(ContextEvent.DESTROY);
    }

    /**
     * Create stages for specified canvas objects.
     * @param {Array} canvasIds Ids of canvas objects
     * @param {Boolean} enableTouch 
     * @returns {Array} Array of stages
     */
    createStages(canvasIds, enableTouch) {
        //NB: canvasIds should be ordered by ascending z-index for mouse interaction to work properly!
        var stages = {};
        var stageLast;
        this.canvases = [];//keep ref for resizing
        this.stages = stages;//keep ref for resizing
        for (var i = 0; i < canvasIds.length; i++) {
            var id = canvasIds[i];
            var canvas = document.getElementById(id);
            this.canvases.push(canvas);
            var stage = new createjs.Stage(canvas);
            stages[id] = stage;
            var view = this.generateContextView(id);
            stage.addChild(view);
            // Keep tracking the mouse even when it leaves the canvas
            stage.mouseMoveOutside = true;
            // Can be disabled if you know the project is desktop ONLY. Enabling touch is a performance hit.
            if (enableTouch) createjs.Touch.enable(stage);
            //
            if (stageLast) {
                stage.nextStage = stageLast;//this will detect mouseevents through stages
                //Make sure the zorder of the stages doesnt change because the
                //stage.nextStage stuff will become invalid!
            }
            stageLast = stage;
        }
        return stages;
    }

    generateContextView() {
        return new createjs.Container();
    }

    initDebug() {
        //Listen to debug key strokes
        window.addEventListener("keyup", this.handleKeyUp.bind(this));
    }


    addStats() {
        this.stats = new Stats();

        const domElement = this.stats.dom;
        domElement.style.top = null;
        domElement.style.left = null;
        domElement.style.right = '0px';
        domElement.style.bottom = '0px';

        document.body.appendChild(domElement);

        log.info("[Main]", "addStats", this.stats);

        if (!this.updateStats) {
            this.updateStats = () => { this.stats.update(); }
        }
        createjs.Ticker.addEventListener("tick", this.updateStats);
    }

    removeStats() {
        if (this.stats) {
            document.body.removeChild(this.stats.domElement);
            this.stats = null;
            createjs.Ticker.removeEventListener("tick", this.updateStats);
        }
    }

    handleKeyUp(e) {
        //only while DEBUG true!
        if (e.altKey && e.ctrlKey && e.shiftKey) {
            var c = String.fromCharCode(e.which);
            if (c == "P")
                this.dispatcher.dispatch(GameEvent.PAUSE_GAME);//pause game
            else if (c == "R")
                this.dispatcher.dispatch(GameEvent.RESUME_GAME);//resume
        }
    }

    setupCanvasForMobile() {
        for (let i = 0; i < canvasIds.length; i++) {
            const obj = document.getElementById(canvasIds[i]);
            const block = function(e) { return false; };
            obj.ontouchstart = block;
            obj.ontouchmove = block;
            obj.onselectstart = block; // prevent DOM around the canvas to be selected when the canvas is double clicked
        }
    }

    initResizing() {
        const layoutManager = this.injector.getValue("layoutManager");
        if (layoutManager) {
            //Check debug values:
            if (!isNaN(parseFloat(getURLParameter("minratio")))) {
                MIN_RATIO = parseFloat(getURLParameter("minratio"));
            }
            if (!isNaN(parseFloat(getURLParameter("maxratio")))) {
                MAX_RATIO = parseFloat(getURLParameter("maxratio"));
            }
            console.log(`App.resize, applying min and max ratio: ${MIN_RATIO}, ${MAX_RATIO}`);

            this.layoutManager = layoutManager;
            this.handleResizeBound = this.handleResize.bind(this);
            window.onorientationchange = this.handleResizeBound;
            window.onresize = this.handleResizeBound;
            this.resize();
        }
    }

    handleResize(e) {
        this.clearResizeTimeout();
        this._timeoutResize = setTimeout(this.resize.bind(this), 70);
    }

    clearResizeTimeout() {
        if (this._timeoutResize != null) {
            clearTimeout(this._timeoutResize);
            this._timeoutResize = null;
        }
    }

    resize() {
        let w = window.innerWidth, h = window.innerHeight;
        const dpr = window.devicePixelRatio;
        const ratio = w / h;
        const canvases = this.canvases;
        //Read original dimensions if first time
        const canvas = canvases[0];
        if (!this.widthOriginal) {
            //NB original size is size of 'critical'/'design' area (1080:1742)
            this.widthOriginal = canvas.clientWidth;
            this.heightOriginal = canvas.clientHeight;
        }
        const ow = this.widthOriginal, oh = this.heightOriginal;
        //
        let scale, cw, ch, cr;
        if (!AppModel.isPhone) {
            //Desktop and tablets
            //  Use 'design' ratio (1080:1742) 
            //  Apply min and max size
            //  Also scale pageframing/background image same as canvas
            const s = Math.min(1, w / h > MAX_WIDTH / MAX_HEIGHT ? h / MAX_HEIGHT : w / MAX_WIDTH);
            cw = MAX_WIDTH * s;
            ch = MAX_HEIGHT * s;
            cr = cw / ch;
            //
            scale = cw / ow;
            //Also scale background image
            //BW and BH give the true size of the background image, which is designed to contain the MAX_W x MAX_H canvas
            const BW = 3200, BH = 1800;
            const size = Math.round(BW * s) + "px " + Math.round(BH * s) + "px";
            document.getElementById("backgroundContainer").style.backgroundSize = size;
        }
        else {
            //Mobile phones
            //  If landscape, show message to rotate screen
            //  Contain original (design) viewport within current viewport
            //  Take into account a margin to support more or less similar aspect ratios (MIN & MAX_RATIO)
            cr = Math.max(Math.min(MAX_RATIO, ratio), MIN_RATIO);
            cw = w;
            ch = h;
            scale = 1;
            // MAX_WIDTH/MAX_HEIGHT is design ratio
            if (ratio > ow / oh) {
                cw = cr * ch;
                scale = ch / oh;
            }
            else {
                ch = cw / cr;
                scale = cw / ow;
            }
            //Check if we have to show rotation image:
            this.checkMobileOrientation(w, h);
        }
        const ox = .5 * (w - cw), oy = .5 * (h - ch);//centered
        //
        console.log("App.handleResize ", w, h + " , " + dpr + " , " + ow, oh + " , " + cw, ch + " , " + ox, oy + " , " + cr, scale);
        //
        const container = canvas.parentElement;
        container.style.width = cw + "px";
        container.style.height = ch + "px";
        container.style.left = ox + "px";
        container.style.top = oy + "px";
        //
        for (var i = 0; i < canvases.length; i++) {
            let canvas = canvases[i];
            canvas.getContext('2d').scale(dpr, dpr);
            canvas.width = cw * dpr;
            canvas.height = ch * dpr;
            //size in DOM:
            canvas.style.width = cw + "px";
            canvas.style.height = ch + "px";
        }
        for (let id in this.stages) {
            const stage = this.stages[id];
            stage.x = cw * dpr / 2;
            stage.y = ch * dpr / 2;
            stage.scaleX = stage.scaleY = scale * dpr;
        }

        //Set unscaled viewport in layoutManager    
        const f = 1 / scale;
        //NB: values for viewportOriginal in stage coordspace
        this.layoutManager.setViewport(-.5 * cw * f, -.5 * ch * f, cw * f, ch * f, -.5 * ow, -.5 * oh, ow, oh);
        //TEST:
        //stage.addChild(new createjs.Shape()).graphics.s("red").ss(10).r(-.5*cw*f, -.5*ch*f, cw*f, ch*f);
    }

    checkMobileOrientation(w, h) {
        const show = w > h;
        document.getElementById("rotateContainer").style.display = show ? "block" : "none";
    }

    handleVisibilityChange(e) {
        //console.log("App.handleVisibilityChange, hidden: " + document.hidden);
        const hidden = document.hidden;
        const soundManager = this.injector.getValue('soundManager');
        if (hidden) {
            this._mutedBeforeHidden = soundManager.getMute();
            soundManager.setMute(true);
        }
        else {
            soundManager.setMute(this._mutedBeforeHidden);
        }
    }

}

//Resize configuration
let MIN_RATIO = .5124;//phones
let MAX_RATIO = .7653;//phones
const MAX_WIDTH = 1080;//desktop
const MAX_HEIGHT = 1742;//desktop